import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronRight, ListChecks, RefreshCw } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { listCertifications } from "@/lib/certification";
import { fetchFileRequests } from "@/lib/fileRequestsClient";
import { EMAIL_INTAKE_BRANDS, fetchEmailIntake } from "@/lib/emailIntake";
import { RECOUP_FORM_TYPE, recoupDraftId } from "@/lib/budgetRecoup";
import { DISBURSEMENT_FORM_TYPE, disbursementDraftId } from "@/lib/disbursements";
import { AID_WEEK_FORM_TYPE } from "@/lib/billingAidStatus";
import { VALS_DIARY_FORM_TYPE } from "@/lib/valsDiaryWeeks";
import { getClaimAvailabilityTable } from "@/lib/aidPlannerCriminalFees";
import { SUMMARY_FIRMS, backlogText, buildMorningSummary } from "@/lib/morningSummary";
import { asList, getJson, loadSources, perFirm, portalDrafts, sharedDrafts as drafts } from "@/lib/summarySources";
import { requestFocus } from "@/lib/portalFocus";

// The morning summary at the top of the admin home (src/lib/morningSummary.js decides what
// goes in it). Everything is read when the home opens; coming back from a tool within a few
// minutes reuses what was read, and Refresh reads it all again.

const CACHE_MS = 5 * 60 * 1000;
let cached = null;

const SOURCES = {
  requests: () => getJson("/api/portal-requests?limit=500").then(asList),
  certifications: () => perFirm(SUMMARY_FIRMS, (brand) => listCertifications(brand).then((body) => asList(body?.certifications))),
  matters: () => getJson("/api/aid-planner-matters").then(asList),
  diaryWeeks: () => perFirm(["vals", "eas"], (brand) => portalDrafts(brand, VALS_DIARY_FORM_TYPE)),
  fileRequests: () => perFirm(SUMMARY_FIRMS, (brand) => fetchFileRequests(brand).then((result) => result.requests || [])),
  intake: () => perFirm(EMAIL_INTAKE_BRANDS, (brand) => fetchEmailIntake(brand)),
  recoup: () => perFirm(SUMMARY_FIRMS, (brand) =>
    drafts(brand, RECOUP_FORM_TYPE).then((rows) => rows.find((row) => row.id === recoupDraftId(brand))?.form_data || null)),
  disbursements: () => perFirm(["lacw", "vals", "eas"], (brand) =>
    drafts(brand, DISBURSEMENT_FORM_TYPE).then((rows) => rows.find((row) => row.id === disbursementDraftId(brand))?.form_data || null)),
  aidWeeks: () => drafts("lacw", AID_WEEK_FORM_TYPE),
  heldItems: () => drafts("admin", "lacw_pending_aid").then((rows) => asList(rows[0]?.form_data?.items)),
};

function greeting(date) {
  const hour = date.getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

const ROW_CLASS = "flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-neutral-400";

// A line about one item opens that item; a line about several opens a list of them, each
// opening its own (src/lib/portalFocus.js); any other line opens its page.
function SummaryRow({ entry, onOpen, primary }) {
  const [listOpen, setListOpen] = useState(false);
  const targets = entry.targets || [];
  const openTarget = (target) => {
    requestFocus(entry.focus, entry.brand, target.id);
    onOpen(entry.tab);
  };
  const several = targets.length > 1;
  const onClick = several ? () => setListOpen((open) => !open) : targets.length === 1 ? () => openTarget(targets[0]) : () => onOpen(entry.tab);

  return (
    <li className="border-t border-neutral-200 first:border-t-0">
      <button type="button" onClick={onClick} aria-expanded={several ? listOpen : undefined} className={ROW_CLASS}>
        <span className="min-w-[3.25rem] rounded-md bg-neutral-100 px-2 py-0.5 text-center text-[11px] font-semibold text-neutral-600">
          {entry.firm}
        </span>
        <span className="min-w-0 flex-1 text-sm text-neutral-900">
          {entry.text}
          {entry.detail && <span className="block text-xs text-neutral-500">{entry.detail}</span>}
        </span>
        <span className="flex items-center gap-1 whitespace-nowrap text-xs font-medium" style={{ color: primary }}>
          {several ? (
            <>
              {listOpen ? "Hide" : `Show ${targets.length}`}
              {listOpen ? <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" /> : <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />}
            </>
          ) : (
            <>{targets.length === 1 ? "Open" : entry.page} <span aria-hidden="true">→</span></>
          )}
        </span>
      </button>
      {several && listOpen && (
        <ul className="border-t border-neutral-100 bg-neutral-50/60 pb-1">
          {targets.map((target) => (
            <li key={target.id}>
              <button
                type="button"
                onClick={() => openTarget(target)}
                className="flex w-full items-center gap-3 py-2 pl-[5.5rem] pr-4 text-left transition hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-neutral-400"
              >
                <span className="min-w-0 flex-1 text-sm text-neutral-900">
                  {target.label}
                  {target.detail && <span className="block text-xs text-neutral-500">{target.detail}</span>}
                </span>
                <span className="whitespace-nowrap text-xs font-medium" style={{ color: primary }}>
                  Open <span aria-hidden="true">→</span>
                </span>
              </button>
            </li>
          ))}
          <li>
            <button type="button" onClick={() => onOpen(entry.tab)} className="py-1.5 pl-[5.5rem] pr-4 text-xs font-medium hover:underline" style={{ color: primary }}>
              Or open {entry.page} <span aria-hidden="true">→</span>
            </button>
          </li>
        </ul>
      )}
    </li>
  );
}

export default function MorningSummary({ onOpen, primary = "#2563eb" }) {
  const { user } = useAuth();
  const [state, setState] = useState(() => (cached ? { status: "ready", ...cached } : { status: "loading" }));

  const load = useCallback(async (force = false) => {
    if (!force && cached && Date.now() - cached.at.getTime() < CACHE_MS) {
      setState({ status: "ready", ...cached });
      return;
    }
    setState((current) => ({ ...current, status: current.data ? "refreshing" : "loading" }));
    const data = await loadSources(SOURCES, "Morning summary");
    cached = { data, at: new Date() };
    setState({ status: "ready", ...cached });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const now = new Date();
  const firstName = String(user?.full_name || user?.name || "").trim().split(/\s+/)[0];
  const summary = state.data
    ? buildMorningSummary(state.data, { today: now, availabilityFor: getClaimAvailabilityTable })
    : null;
  const backlog = summary ? backlogText(summary.backlog) : "";

  return (
    <section aria-labelledby="morning-summary-heading" className="mb-12 rounded-xl bg-neutral-50 p-5 sm:p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 id="morning-summary-heading" className="text-2xl font-semibold tracking-tight text-neutral-900 sm:text-3xl">
          {greeting(now)}{firstName ? `, ${firstName}` : ""}
        </h2>
        <span className="flex items-center gap-2 text-xs text-neutral-500">
          {now.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long" })}
          {state.at && ` · updated ${state.at.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" })}`}
          <button
            type="button"
            onClick={() => load(true)}
            disabled={state.status !== "ready"}
            className="rounded-md p-1 text-neutral-500 transition hover:bg-neutral-200 hover:text-neutral-800 disabled:opacity-50"
            aria-label="Refresh the summary"
            title="Refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${state.status !== "ready" ? "animate-spin" : ""}`} aria-hidden="true" />
          </button>
        </span>
      </div>

      {!summary ? (
        <p className="mt-3 text-sm text-neutral-500">Checking what's outstanding across LACW, VALS, EAS and RGA…</p>
      ) : (
        <>
          <p className="mt-1 mb-5 flex flex-wrap items-baseline justify-between gap-2 text-sm text-neutral-500">
            <span>{summary.urgent.length} urgent, {summary.notUrgent.length} not urgent, across LACW, VALS, EAS and RGA.</span>
            {/* What was done, to go with what is waiting. */}
            <button type="button" onClick={() => onOpen("weekly-summary")} className="text-xs font-medium hover:underline" style={{ color: primary }}>
              Weekly summary of work done <span aria-hidden="true">→</span>
            </button>
          </p>

          {summary.unavailable.length > 0 && (
            <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800">
              Couldn't check {summary.unavailable.join(", ")} just now, so anything there isn't shown. Refresh to try again.
            </p>
          )}

          <div className="mb-4 overflow-hidden rounded-xl border border-red-200 bg-white">
            <h3 className="flex items-center gap-2 bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-800">
              <AlertTriangle className="h-4 w-4" aria-hidden="true" />
              Urgent <span className="font-normal">· {summary.urgent.length}</span>
            </h3>
            {summary.urgent.length ? (
              <ul>
                {summary.urgent.map((entry) => <SummaryRow key={entry.id} entry={entry} onOpen={onOpen} primary={primary} />)}
              </ul>
            ) : (
              <p className="px-4 py-3 text-sm text-neutral-500">Nothing urgent this morning.</p>
            )}
          </div>

          <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
            <h3 className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-neutral-900">
              <ListChecks className="h-4 w-4" aria-hidden="true" />
              Not urgent <span className="font-normal text-neutral-500">· {summary.notUrgent.length}</span>
            </h3>
            {summary.notUrgent.length > 0 && (
              <ul className="border-t border-neutral-200">
                {summary.notUrgent.map((entry) => <SummaryRow key={entry.id} entry={entry} onOpen={onOpen} primary={primary} />)}
              </ul>
            )}
            {(backlog || summary.allClear.length > 0) && (
              <p className="border-t border-neutral-200 px-4 py-2.5 text-xs text-neutral-500">
                {backlog && `Older backlog: ${backlog}.`}
                {backlog && summary.allClear.length > 0 && " "}
                {summary.allClear.length > 0 && `Nothing open: ${summary.allClear.join(", ")}.`}
              </p>
            )}
          </div>
        </>
      )}
    </section>
  );
}
